/**
 * DSL_Cards
 * HUD-style card component
 * Renders multiple cards from JSON data inside DSL_FullScreenWindow
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { trackEvent } from '../../utils/analytics'
import DSL_FullScreenWindow from './DSL_FullScreenWindow'
import { playSound } from '../../utils/soundFX'
import StackedCard from './StackedCard'
import DSLBadge from './DSLBadge'
import DSLCardLogo from './DSLCardLogo'
import DSLBadgeKey from './DSLBadgeKey'

const HUD_COLOR = '#ee3a17'

function HorizontalScrollBar({ scrollContainerRef }) {
  const [thumbLeft, setThumbLeft] = useState(0)
  const [thumbWidth, setThumbWidth] = useState(10)
  const [isVisible, setIsVisible] = useState(false)

  const update = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    if (scrollWidth <= clientWidth) { setIsVisible(false); return }
    setIsVisible(true)
    // All values as percentages of the track width — thumb can never overflow
    const widthPct = (clientWidth / scrollWidth) * 100
    const maxScroll = scrollWidth - clientWidth
    const leftPct = (scrollLeft / maxScroll) * (100 - widthPct)
    setThumbWidth(Math.max(4, widthPct))
    setThumbLeft(leftPct)
  }, [scrollContainerRef])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    update()
    el.addEventListener('scroll', update)
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', update); ro.disconnect() }
  }, [scrollContainerRef, update])

  return (
    <div className="starcade-hscrollbar" style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
      <div className="starcade-hscrollbar-track">
        <div
          className="starcade-hscrollbar-thumb"
          style={{ left: `${thumbLeft}%`, width: `${thumbWidth}%` }}
        />
      </div>
    </div>
  )
}

export default function DSL_Cards({ section = 'starcade', data, isTransitioning = false, onClose, isClosing = false, scrollContainerRef }) {
  // Enable flicker effects for all devices (memory leaks fixed)
  const shouldFlicker = isTransitioning

  // Drag-to-scroll state — tracked in refs to avoid re-renders
  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const scrollStartX = useRef(0)
  const dragDistance = useRef(0)

  // Shared ref: either the passed-in ref or a local one
  const localRef = useRef(null)
  const containerRef = scrollContainerRef ?? localRef

  const onMouseDown = useCallback((e) => {
    const el = containerRef.current
    if (!el) return
    isDragging.current = true
    dragStartX.current = e.clientX
    scrollStartX.current = el.scrollLeft
    dragDistance.current = 0
    el.style.cursor = 'grabbing'
    el.style.userSelect = 'none'
  }, [containerRef])

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return
    const el = containerRef.current
    if (!el) return
    const dx = e.clientX - dragStartX.current
    dragDistance.current = Math.abs(dx)
    el.scrollLeft = scrollStartX.current - dx
  }, [containerRef])

  const onMouseUp = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    isDragging.current = false
    el.style.cursor = ''
    el.style.userSelect = ''
  }, [containerRef])

  const onMouseLeave = useCallback(() => {
    const el = containerRef.current
    if (!isDragging.current || !el) return
    isDragging.current = false
    el.style.cursor = ''
    el.style.userSelect = ''
  }, [containerRef])

  return (
    <DSL_FullScreenWindow onClose={onClose} isClosing={isClosing}>
      <div className="starcade-landscape-message">
        PLEASE ROTATE TO PORTRAIT MODE
      </div>
      <div className="tablet-portrait-message">
        PLEASE ROTATE TO LANDSCAPE MODE
      </div>
      <div className="starcade-carousel-wrapper">
        <div className="starcade-carousel-inner">
          <div className="starcade-cards-overflow-host">
            <div
              ref={containerRef}
              className="starcade-cards-scroll-container"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
            >
              {data.map((item, index) => (
                <StarcadeCard
                  key={item.id}
                  item={item}
                  section={section}
                  isTransitioning={isTransitioning}
                  shouldFlicker={shouldFlicker}
                  index={index}
                  dragDistanceRef={dragDistance}
                />
              ))}
            </div>
          </div>
          <HorizontalScrollBar scrollContainerRef={containerRef} />
          <DSLBadgeKey />
        </div>
      </div>
    </DSL_FullScreenWindow>
  )
}

function StarcadeCard({ item, section, isTransitioning, shouldFlicker, index, dragDistanceRef }) {
  // Stacked card type — delegate to StackedCard component
  if (item.cardType === 'stacked') {
    return (
      <StackedCard
        item={item}
        section={section}
        isTransitioning={isTransitioning}
        shouldFlicker={shouldFlicker}
        index={index}
        dragDistanceRef={dragDistanceRef}
      />
    )
  }

  const openUrl = () => {
    // Suppress click if the user dragged more than 5px
    if (dragDistanceRef.current > 5) return
    if (!item.buttonUrl) return
    trackEvent(`${section}_${item.id}`)
    playSound('DataPoint_Open')
    window.open(item.buttonUrl, '_blank', 'noopener,noreferrer')
  }

  // Generate random animation delays for each card element
  const randomDelays = {
    outerBorder: Math.random() * 0.3,
    gradient: Math.random() * 0.3,
    text: Math.random() * 0.3
  }
  const scaleDuration = (0.8 + Math.random() * 0.4) * 0.4
  const imageDelay = scaleDuration * 0.6

  return (
    <div
      className={`starcade-card ${shouldFlicker ? 'flickering' : ''}`}
      onClick={openUrl}
      style={{
        cursor: 'pointer',
        '--flicker-delay-outer': `${randomDelays.outerBorder}s`,
        '--flicker-delay-gradient': `${randomDelays.gradient}s`,
        '--flicker-delay-text': `${randomDelays.text}s`,
        '--card-scale-duration': `${scaleDuration.toFixed(3)}s`,
        '--image-delay': `${imageDelay.toFixed(3)}s`
      }}
    >
      <StarcadeFrame
        isTransitioning={isTransitioning}
        shouldFlicker={shouldFlicker}
        index={index}
        title={item.title}
        imageSrc={item.imageSrc}
        imageScale={item.imageScale ?? 1}
        imageOffsetX={item.imageOffsetX ?? 0}
        imageOffsetY={item.imageOffsetY ?? 0}
        badge={item.badge}
        logo={item.logo}
        logoWidth={item.logoWidth ?? 48}
        logoOffsetY={item.logoOffsetY ?? 0}
        titleOffsetX={item.titleOffsetX ?? 0}
        titleOffsetY={item.titleOffsetY ?? 0}
        showCenterTitle={item.titleVisibleOnCard !== false}
        titleDark={item.titleDark === true}
      />
    </div>
  )
}

export function StarcadeFrame({ isTransitioning, shouldFlicker, index, svgId, labelOverride, title, imageSrc, imageScale = 1, imageOffsetX = 0, imageOffsetY = 0, badge, logo = null, logoWidth = 48, logoOffsetY = 0, titleOffsetX = 0, titleOffsetY = 0, showCenterTitle = false, titleDark = false, width: widthProp, height: heightProp }) {
  const width = widthProp ?? 320
  const height = heightProp ?? 480

  // svgId overrides index for SVG element ids (prevents collisions in stacked cards)
  // index is always used for the visible label number
  const idKey = svgId ?? index

  // Original SVG paths have transforms that offset them
  // card_border.svg: transform="matrix(1,0,0,1,-179.583333,-283.06144)"
  // Outer border coordinates range: x(190-2294), y(293-3200)
  const outerPathData = "M291,3200.656L190,3085L190,395L291.522,293.478L2245,293.478L2294.061,342.539L2294.061,2206L2196.941,2303.12L2196.941,3096L2093.648,3199.293L291,3200.656Z"

  // card_inner.svg: transform="matrix(1,0,0,1,-245.833333,-349.31144)"
  // Inner border coordinates range: x(250-2224), y(353-3130)
  // Adjusted inward by ~30 units on all sides for moderate gap
  const innerPathData = "M361,3100.656L280,3015L280,465L361.522,383.478L2155,383.478L2194.061,422.539L2194.061,1156L2096.941,1253.12L2096.941,3046L2043.648,3099.293L361,3100.656Z"

  // Expand viewBox upward by 300 units to make room for the label above the card
  // Original bounds: x=190, y=293, width=2104, height=2907
  const strokePadding = 15
  const labelSpace = 300
  const viewBox = `${190 - strokePadding} ${293 - strokePadding - labelSpace} ${2104 + strokePadding * 2} ${2907 + strokePadding * 2 + labelSpace}`

  // labelOverride lets callers supply a fully-formed label string (e.g. stacked cards use "001-A")
  // Otherwise fall back to the standard "[ 001 ]  TITLE" format
  const paddedIndex = String(index + 1).padStart(3, '0')
  const labelText = labelOverride ?? `[ ${paddedIndex} ]  ${title.toUpperCase()}`

  return (
    <>
      <svg className={`starcade-svg-frame ${shouldFlicker ? 'flickering' : ''}`} width={width} height={height} viewBox={viewBox} data-index={index}>
      <defs>
        <linearGradient className="card-gradient" id={`cardGradient-${idKey}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={HUD_COLOR} stopOpacity="0.75" />
          <stop offset="100%" stopColor={HUD_COLOR} stopOpacity="0.25" />
        </linearGradient>

        {/* Image fills the inner path area, scaled around its centre */}
        {(() => {
          const baseX = 280, baseY = 383, baseW = 1914, baseH = 2717
          const scaledW = baseW / imageScale
          const scaledH = baseH / imageScale
          const offsetX = baseX + (baseW - scaledW) / 2 + imageOffsetX
          const offsetY = baseY + (baseH - scaledH) / 2 + imageOffsetY
          return (
            <pattern id={`imagePattern-${idKey}`} patternUnits="userSpaceOnUse" x={offsetX} y={offsetY} width={scaledW} height={scaledH}>
              <image
                className="card-image"
                href={imageSrc}
                x="0"
                y="0"
                width={scaledW}
                height={scaledH}
                preserveAspectRatio="xMidYMid slice"
              />
            </pattern>
          )
        })()}
      </defs>

      {/* HUD label above the card — [ 001 ] TITLE, left-aligned with outer path */}
      <text
        className="card-text"
        x={190 - strokePadding + 10}
        y={293 - strokePadding - 60}
        textAnchor="start"
        fill="#E3E3E3"
        fontSize="80"
        fontFamily="'IBM Plex Mono', monospace"
        fontWeight="400"
        letterSpacing="6"
        opacity="0.9"
      >
        {labelText}
      </text>

      {/* Outer border with gradient fill - brand-red-80 */}
      <path
        className="card-outer-border"
        d={outerPathData}
        fill={`url(#cardGradient-${idKey})`}
        stroke="rgba(238, 58, 23, 0.8)"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Inner border - filled with image - brand-red-20 */}
      <path
        className="card-inner-border"
        d={innerPathData}
        fill={`url(#imagePattern-${idKey})`}
        stroke="rgba(238, 58, 23, 0.2)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="1"
      />
    </svg>
    <DSLBadge type={badge} />
    <DSLCardLogo src={logo} width={logoWidth} offsetY={logoOffsetY} />
    {showCenterTitle && title && (
      <div
        className={`dsl-card-center-title${titleDark ? ' dsl-card-center-title--dark' : ''}`}
        style={{
          '--title-offset-x': `${titleOffsetX}px`,
          '--title-offset-y': `${titleOffsetY}px`
        }}
      >
        {title}
      </div>
    )}
    </>
  )
}
